<template>
  <section
    id="checkoutPayment"
    :class="[openClass, 'expandable_section']"
  >
    <h2
      class="expandable_header"
      @click="updateSection(sectionName)"
    >
      {{ $t("checkout.payment.paymentMethod", { number: number }) }}
    </h2>

    <div class="expandable_content review-payment">
      <!-- Docusign Status Loader -->
      <div
        v-if="loadingContent || loadingDocusign"
        class="docusign-status-loader"
      />

      <div v-else>
        <!-- Form -->
        <div v-if="valid && coveredByCredit">
          <button
            class="continue-button btn btn-primary"
            @click="placeOrder(null)"
          >
            {{ placeOrderText }}
          </button>
        </div>
        <div v-else-if="valid">
          <p
            :class="[errs == null ? '' : 'errors']"
            @click="handleError(errs)"
          >
            {{ errs }}
          </p>

          <img
            src="https://d2xpg1khvwxlf1.cloudfront.net/production/images/original/16162-credit-cards-f4bc8299b8a663920defb494900e90e3.png"
            alt="Credit Cards"
          >

          <StripeForm
            v-if="selected"
            :address="billing.address1"
            :address2="billing.address2"
            :buttontext="placeOrderText"
            :city="billing.city"
            :name="name"
            :phone="billing.phoneNumber"
            :state="billing.state"
            :zipcode="billing.zipCode"
            includechecks="true"
            @card-added="placeOrder"
            @update-f1="updateF1"
          />
        </div>

        <!-- Missing Values -->
        <button
          v-if="amountMissing"
          class="continue-button btn btn-primary"
          @click="updateSection('review')"
        >
          {{ $t('checkout.payment.reviewOrder') }}
        </button>

        <button
          v-if="billingInfoMissing"
          class="continue-button btn btn-primary"
          @click="updateSection('address')"
        >
          {{ $t("checkout.payment.updateInfo") }}
        </button>

        <button
          v-if="docusignMissing"
          class="continue-button btn btn-primary"
          @click="updateSection('termsAndConditions')"
        >
          {{ $t("checkout.docusign.sectionTitle") }}
        </button>

        <div
          v-if="card.id != undefined"
          class="processing-payment loading"
        >
          <div class="loading-icon" />

          <button
            disabled
            class="continue-button continue-button btn btn-primary"
          >
            {{ $t("checkout.payment.processing",
                  { amount: paymentfields.amountDecorated, numbers: card.last_4 }) }}
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
/* global ga pi Turbolinks */

import Api from '../cart/cart_api'
import CountryData from '../country_data'
import StripeForm from '../stripe_form/credit_card_form.vue'
import { mapState, mapActions } from 'vuex'

export default {
  components: {
    StripeForm
  },

  props: {
    number: {
      default: null,
      required: false,
      type: Number
    },
  },

  data() {
    return {
      card: {},
      errs: null,
      f1Consent: false,
      pardot_id: '',
      sectionName: 'payment',
      loadingContent: false
    }
  },

  computed: {
    ...mapState('checkout', {
      paymentfields: state => state.paymentFields,
      billing: state => state.billing,
      docusignStatus: state => state.docusignStatus,
      loadingDocusign: state => state.loadingDocusignStatus
    }),
    amountMissing() {
      return this.paymentfields.amount == undefined ||
             this.paymentfields.amount == null ||
            (this.paymentfields.amount < 1 && !this.$store.state.checkout.applyCredit)
    },

    billingInfoMissing() {
      return jQuery.isEmptyObject(this.billing.firstName) ||
             jQuery.isEmptyObject(this.billing.lastName) ||
             jQuery.isEmptyObject(this.billing.phoneNumber) ||
             jQuery.isEmptyObject(this.billing.address1) ||
             jQuery.isEmptyObject(this.billing.city) ||
             this.billing.state == 'missing state value' ||
             jQuery.isEmptyObject(this.billing.city) ||
             this.invalidCountry ||
             this.invalidPostalCode
    },

    creditsUsed(){
      if(this.$store.state.checkout.applyCredit){
        return Math.min(this.$store.state.checkout.creditAmount,this.$store.state.checkout.orderTotal)
      }

      return 0
    },

    coveredByCredit() {
      if(this.$store){
        return this.$store.state.checkout.creditAmount >= this.$store.state.checkout.orderTotal
      }

      return false
    },

    docusignMissing() {
      return this.docusignStatus !== 'completed' ? true : false
    },

    formData() {
      return {
        checkout: {
          accepted_terms: true,
          address: this.billing.address1,
          address2: this.billing.address2,
          amount: this.paymentfields.amount,
          card_id: this.card.id,
          city: this.billing.city,
          company_name: this.billing.companyName,
          country: this.billing.country,
          paid_by_credits: this.coveredByCredit,
          credits: this.creditsUsed,
          f1_consent: this.f1Consent,
          first_name: this.billing.firstName,
          ga_id: this.gaId,
          gcl_id: window.localStorage.gclid,
          last_name: this.billing.lastName,
          order_id: this.paymentfields.orderId,
          pardot_id: this.pardotID(),
          phone_number: this.billing.phoneNumber,
          purchase_order_number: this.billing.purchaseOrderNumber,
          room_information: this.paymentfields.roomInformation,
          state: this.billing.state,
          zip_code: this.billing.zipCode
        }
      }
    },

    gaId() {
      if (typeof ga === 'function') {
        return ga(function(tracker) { return tracker.get('clientId') } )
      } else {
        return null
      }
    },

    invalidCountry() {
      return CountryData.blockedCountries().concat(
        ['', null, undefined]).includes(this.billing.country)
    },

    invalidPostalCode() {
      if (['CA', 'US'].includes(this.billing.country)) {
        return jQuery.isEmptyObject(this.billing.zipCode)
      } else {
        // We only require code of US and Canada so empty zip is valid in all other cases
        return false
      }
    },

    name() {
      return `${this.billing.firstName} ${this.billing.lastName}`
    },

    openClass() {
      return (this.selected) ? 'open' : ''
    },

    placeOrderText() {
      return this.$t('checkout.payment.placeOrder', { amount: '' })
    },

    selected() {
      return this.$store.state.checkout.section == this.sectionName
    },

    valid() {
      return this.card.id == undefined &&
             !this.amountMissing &&
             !this.billingInfoMissing &&
             !this.docusignMissing
    },
  },

  mounted() {
    const query = window.location.search
    const params = new URLSearchParams(query)

    if (params.get('event') === 'signing_complete') {
      this.loadingContent = true
      setTimeout(() => {
        this.loadingContent = false
      }, 5000)
    }
  },

  methods: {
    ...mapActions('checkout', ['updateSection']),
    isEmpty(obj) {
      for(var key in obj) {
        // eslint-disable-next-line no-prototype-builtins
        if(obj.hasOwnProperty(key))
          return false
      }
      return true
    },
    confirmPayment(paymentTermId, paymentIntentId) {
      let body = JSON.stringify({ checkout: {
        f1_consent: this.f1Consent,
        payment_intent_id: paymentIntentId,
        payment_term_id: paymentTermId,
      }})

      Api.checkoutConfirm(body).then(order => {
        if (order.redirect) {
          Turbolinks.visit(order.redirect)
        } else if (order.error) {
          this.card = {}
          this.errs = order.error
        } else {
          this.card = {}
          this.errs = this.$t('checkout.payment.couldNotProcess')
        }
      })
    },

    handleError(errs){
      if(errs == this.$t('checkout.payment.hotelNotAvailable')){
        Api.getOrder().then(order=>{
          location.pathname=`cart/items/${order.items[0].id}/hotels`
        })
      } else{
        errs=null
      }
    },

    handleSCA(order) {
      this.card.stripe.paymentIntentSCA(order.client_secret).then(result => {
        if (result.error) {
          this.card = {}
          this.errs = result.error.message
        } else {
          this.confirmPayment(order.payment_term_id, result.paymentIntent.id)
        }
      })
    },

    placeOrder(card) {
      if (!this.valid) { return }
      card ? this.placeOrderWithCard(card) : this.placeOrderWithCredit()
    },

    placeOrderWithCredit(){
      Api.checkout(JSON.stringify(this.formData)).then(order => {
        if (order.redirect) {
          window.location.replace(order.redirect)
        } else if (order.client_secret) {
          this.handleSCA(order)
        } else if (order.error) {
          this.errs = order.error
        } else {
          this.errs = this.$t('checkout.payment.couldNotProcess')
        }
      })
    },

    placeOrderWithCard(card){
      this.card = card

      Api.checkout(JSON.stringify(this.formData)).then(order => {
        if (order.redirect) {
          window.location.replace(order.redirect)
        } else if (order.client_secret) {
          this.handleSCA(order)
        } else if (order.error) {
          this.card = {}
          this.errs = order.error
        } else {
          this.card = {}
          this.errs = this.$t('checkout.payment.couldNotProcess')
        }
      })
    },

    pardotID() {
      try {
        return pi.tracker.visitor_id
      } catch (_) {
        null
      }
    },

    updateF1(bool) {
      this.f1Consent = bool
    }
  }
}
</script>
